import React, { useRef, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import usePermissions from '../hooks/usePermissions';
import useGetUserRole from '../hooks/useGetUserRole';
import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import { fileToBase64 } from '../helpers/convertToBase64';
import tinyMCEConfigs from '../utils/tinymce';

const schema = yup
  .object({
    moduleName: yup.string().required('This is a required field'),
    moduleShortDescription: yup.string().required('This is a required field'),
    skillLevel: yup.string().required('This is a required field'),
    moduleImage: yup
      .mixed()
      .required('Please select an image')
      .test('fileSize', 'Please upload image less than 4MB', (value) => {
        if (!value || value.length === 0) {
          return true;
        }
        return value[0].size <= 4000000;
      }),
    intro_video_url: yup.string().required('This is a required field'),
  })
  .required();

function EditCourseModule({ toggleModal, module }) {
  //   const [isSubmitting, setIsSubmitting] = useState(false);
  const { moduleId } = useParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const permissions = usePermissions();
  const role = useGetUserRole();

  const canUpdateCourseModule =
    permissions?.includes('UpdateCourseModule') || role === 'SuperAdmin';

  const queryClient = useQueryClient();
  const onModuleCreationSuccess = () => {
    toast.success('Module successfully updated!');
    toggleModal();
  };
  const onModuleCreationFailure = () => {
    toast.error("Couldn't update module! Please try again");
  };

  const { mutate, isLoading: isSubmitting } = useMutation(
    (data) => {
      return axiosInstancev2.put(`/course-modules/${moduleId}`, data);
    },
    {
      onSuccess: () => {
        onModuleCreationSuccess();
        queryClient.invalidateQueries(['module', module?.id]);
      },
      onError: onModuleCreationFailure,
    }
  );

  const [moduleDescription, setModuleDescription] = useState(
    module?.moduleDescription
  );
  const [requirements, setRequirements] = useState(module?.requirements);

  const [descriptionIsDirty, setDescriptionIsDirty] = useState(false);
  const [requirementsIsDirty, setRequirementsIsDirty] = useState(false);

  // wysiwyg
  const descriptionEditorRef = useRef(null);
  const requirementsEditorRef = useRef(null);

  const saveDescriptionContent = () => {
    if (descriptionEditorRef.current) {
      const content = descriptionEditorRef.current.getContent();
      setDescriptionIsDirty(false);
      descriptionEditorRef.current.setDirty(false);
      setModuleDescription(content);
    }
  };

  const saveRequirementsContent = () => {
    if (requirementsEditorRef.current) {
      const content = requirementsEditorRef.current.getContent();
      setRequirementsIsDirty(false);
      requirementsEditorRef.current.setDirty(false);
      setRequirements(content);
    }
  };

  // check if is empty
  const isDescriptionEmpty =
    moduleDescription === '<p><br></p>' || moduleDescription === '';

  const isRequirementEmpty =
    requirements === '<p><br></p>' || requirements === '';

  // wysiwyg

  const onSubmit = async (data) => {
    const base64Url = data?.moduleImage[0]
      ? await fileToBase64(data.moduleImage[0])
      : '';

    const requestBody = {
      ...data,
      moduleImage: base64Url,
      moduleDescription,
      requirements,
    };
    mutate(requestBody);
  };

  const buttonDisabled =
    isDescriptionEmpty ||
    isRequirementEmpty ||
    descriptionIsDirty ||
    requirementsIsDirty ||
    Object.keys(errors).length > 0;

  return (
    <div>
      <form
        className="text-gray-700 space-y-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col space-y-1">
          <label htmlFor="moduleName" className="text-base">
            Title
          </label>
          <input
            {...register('moduleName', { required: true })}
            type="text"
            id="moduleName"
            placeholder="Module Name"
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.moduleName ? 'border-red-600' : ''
            }`}
            defaultValue={module?.moduleName}
          />
          {errors.moduleName && (
            <span className="text-red-600 text-xs mt-2">
              {errors.moduleName?.message}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col space-y-1">
            <label htmlFor="moduleImage" className="text-base">
              Module Image
            </label>
            <input
              {...register('moduleImage', {
                required: true,
              })}
              accept="image/jgp, image/jpeg,image/webp"
              type="file"
              id="moduleImage"
              className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
                errors.moduleImage ? 'border-red-600' : ''
              }`}
            />
            {errors.moduleImage && (
              <span className="text-red-600 text-xs mt-2">
                {errors.moduleImage?.message}
              </span>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <label htmlFor="intro_video_url" className="text-base">
              Video Url
            </label>
            <input
              {...register('intro_video_url', { required: true })}
              type="text"
              id="intro_video_url"
              placeholder="Video Url"
              className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
                errors.intro_video_url ? 'border-red-600' : ''
              }`}
              defaultValue={module?.intro_video_url}
            />
            {errors.intro_video_url && (
              <span className="text-red-600 text-xs mt-2">
                {errors.intro_video_url?.message}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="skillLevel" className="text-base">
            Skill Level
          </label>
          <select
            {...register('skillLevel', { required: true })}
            required
            name="skillLevel"
            id="skillLevel"
            className="border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none"
            defaultValue={module?.skillLevel}
          >
            <option value="">Select Option</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          {errors.skillLevel && (
            <span className="text-red-600 text-xs mt-2">
              {errors.skillLevel?.message}
            </span>
          )}
        </div>

        <div className="flex flex-col space-y-1">
          <label htmlFor="hours_of_learning" className="text-base">
            Duration{' '}
            <span className="text-gray-400 text-sm">
              (e.g. 4 Weeks, 20 Hours, 3 Months)
            </span>
          </label>
          <input
            {...register('hours_of_learning')}
            type="text"
            id="hours_of_learning"
            placeholder="e.g. 4 Weeks"
            className="border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none"
            defaultValue={module?.hours_of_learning || ''}
          />
        </div>

        <div className="flex flex-col space-y-1">
          <label htmlFor="moduleShortDescription" className="text-base">
            Short Description
          </label>
          <textarea
            {...register('moduleShortDescription', { required: true })}
            type="text"
            id="moduleShortDescription"
            placeholder="Module Description"
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.moduleName ? 'border-red-600' : ''
            }`}
            rows={2}
            defaultValue={module?.moduleShortDescription}
          />
          {errors.moduleShortDescription && (
            <span className="text-red-600 text-xs mt-2">
              {errors.moduleShortDescription?.message}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div className="flex flex-col space-y-1 max-w-full">
            <label htmlFor="moduleDescription" className="text-base">
              Module Description
            </label>
            <Editor
              tinymceScriptSrc="/tinymce/tinymce.min.js"
              licenseKey="gpl"
              initialValue={moduleDescription}
              onInit={(evt, editor) => {
                descriptionEditorRef.current = editor;
              }}
              onDirty={() => setDescriptionIsDirty(true)}
              init={{
                ...tinyMCEConfigs,
                min_height: 300,
                max_height: 500,
                editable_root: canUpdateCourseModule,
                save_onsavecallback: saveDescriptionContent,
              }}
            />
            {descriptionIsDirty && (
              <p className="text-persian-400 text-sm">
                You have unsaved Description content!
              </p>
            )}
            {isDescriptionEmpty && (
              <span className="text-red-600 text-xs mt-2">
                Please Enter Module Description
              </span>
            )}
          </div>
          <div className="flex flex-col space-y-1 max-w-full">
            <label htmlFor="requirements" className="text-base">
              Module Requirements{' '}
              <strong className="">
                (Please enter the module prerequisites)
              </strong>
            </label>
            <Editor
              tinymceScriptSrc="/tinymce/tinymce.min.js"
              licenseKey="gpl"
              initialValue={requirements}
              onInit={(evt, editor) => {
                requirementsEditorRef.current = editor;
              }}
              onDirty={() => setRequirementsIsDirty(true)}
              init={{
                ...tinyMCEConfigs,
                min_height: 300,
                max_height: 500,
                editable_root: canUpdateCourseModule,
                save_onsavecallback: saveRequirementsContent,
              }}
            />
            {requirementsIsDirty && (
              <p className="text-persian-400 text-sm">
                You have unsaved Requirements content!
              </p>
            )}
            {isRequirementEmpty && (
              <span className="text-red-600 text-xs mt-2">
                Please Enter module Requirements
              </span>
            )}
          </div>
        </div>

        {isSubmitting ? (
          <div className="w-full flex align-middle justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <div className=" flex justify-between mt-8">
            <button
              className={`border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center `}
              type="button"
              onClick={toggleModal}
            >
              <CancelIcon fontSize="inherit" />
              <p>Cancel</p>
            </button>
            <button
              className={`px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center  ${
                buttonDisabled
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-claret-500'
              }`}
              type="submit"
              disabled={buttonDisabled}
            >
              <SaveIcon fontSize="inherit" />
              <p>Update Course Module</p>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default EditCourseModule;
